import React, { useState, useEffect, useRef } from 'react';

interface SynapticTextProps {
  text: string;
  delay?: number;
  className?: string;
  immediate?: boolean; // If true, starts animation immediately without intersection observer
  onComplete?: () => void; // Callback when typing animation completes
  isMobile?: boolean; // If true, optimizes for mobile layouts with proper text wrapping
}

export const SynapticText: React.FC<SynapticTextProps> = ({ 
  text, 
  delay = 0, 
  className = '',
  immediate = false,
  onComplete,
  isMobile = false
}) => {
  const [visibleChars, setVisibleChars] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [isInView, setIsInView] = useState(immediate);
  const elementRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (immediate) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [immediate]);

  useEffect(() => {
    if (!isInView) return;

    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        setVisibleChars(prev => {
          if (prev >= text.length) {
            setIsComplete(true);
            if (onComplete) {
              setTimeout(onComplete, 200); // Small delay after completion
            }
            clearInterval(interval);
            return prev;
          }
          return prev + 1;
        });
      }, 25); // Speed of materialization - faster typing

      return () => clearInterval(interval);
    }, delay);

    return () => clearTimeout(timer);
  }, [text, delay, isInView]);

  // Simplified mobile experience - just show the text without animation
  if (isMobile) {
    return (
      <span 
        ref={elementRef} 
        className={className}
        style={{
          whiteSpace: 'normal',
          wordBreak: 'normal',
          display: 'inline',
          textWrap: 'balance',
          color: '#ffffff !important' // Force white text for mobile modal
        }}
      >
        {text}
      </span>
    );
  }

  // Desktop experience with full animation
  return (
    <span ref={elementRef} className={`relative ${className}`}>
      {text.split(' ').map((word, wordIndex) => (
        <span 
          key={wordIndex} 
          className="inline-block whitespace-nowrap"
        >
          {word.split('').map((char, charIndex) => {
            const globalIndex = text.split(' ').slice(0, wordIndex).join(' ').length + (wordIndex > 0 ? 1 : 0) + charIndex;
            return (
              <span
                key={charIndex}
                className={`
                  relative inline-block transition-all duration-300
                  ${globalIndex < visibleChars 
                    ? 'opacity-100 !text-white' 
                    : 'opacity-0 text-transparent'
                  }
                  ${globalIndex === visibleChars - 1 && !isComplete 
                    ? '!text-white animate-pulse' 
                    : ''
                  }
                `}
                style={{
                  animationDelay: `${globalIndex * 0.02}s`
                }}
              >
                {char}
                
                {/* Neural firing effect for current character */}
                {globalIndex === visibleChars - 1 && !isComplete && (
                  <span className="absolute inset-0 text-accent animate-ping">
                    {char}
                  </span>
                )}
              </span>
            );
          })}
          {wordIndex < text.split(' ').length - 1 && (
            <span
              className={`
                relative inline-block transition-all duration-300
                ${text.split(' ').slice(0, wordIndex + 1).join(' ').length < visibleChars 
                  ? 'opacity-100' 
                  : 'opacity-0'
                }
              `}
            >
              {'\u00A0'}
            </span>
          )}
        </span>
      ))}
      
      {/* Cursor effect */}
      {!isComplete && (
        <span className="inline-block w-0.5 h-6 bg-primary ml-1 animate-pulse" />
      )}
    </span>
  );
};